import { useState } from 'react';
import { ThumbsUp, Edit3, CheckCircle2, ChevronDown, Music, Loader2 } from 'lucide-react';
import Button from '../common/Button';

const BACKBONE_URL = import.meta.env.VITE_BACKBONE_URL || 'http://localhost:8080';

const GENRE_OPTIONS = [
  'ambient', 'blues', 'classical', 'electronicDance', 'folkCountry',
  'funkSoul', 'jazz', 'latin', 'metal', 'pop', 'rapHipHop',
  'reggae', 'rnb', 'rock', 'singerSongwriter', 'other',
];

const GENRE_LABELS: Record<string, string> = {
  ambient: 'Ambient', blues: 'Blues', classical: 'Classical',
  electronicDance: 'Electronic / Dance', folkCountry: 'Folk / Country',
  funkSoul: 'Funk / Soul', jazz: 'Jazz', latin: 'Latin',
  metal: 'Metal', pop: 'Pop', rapHipHop: 'Rap / Hip-Hop',
  reggae: 'Reggae', rnb: 'R&B', rock: 'Rock',
  singerSongwriter: 'Singer-Songwriter', other: 'Other',
};

interface ClassificationFeedbackProps {
  trackContextId: string;
  genre: string;
  moodTags: string[];
  similarArtists: Array<{ name: string; visualStyleKeywords?: string[] }>;
  instruments: string[];
}

export default function ClassificationFeedback({
  trackContextId,
  genre,
  moodTags,
  similarArtists,
  instruments,
}: ClassificationFeedbackProps) {
  const [mode, setMode] = useState<'review' | 'editing' | 'submitted'>('review');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Correction form state
  const [correctedGenre, setCorrectedGenre] = useState(genre);
  const [correctedArtists, setCorrectedArtists] = useState(
    similarArtists.map(a => a.name).join(', ')
  );
  const [correctedMoods, setCorrectedMoods] = useState(moodTags.join(', '));
  const [correctedInstruments, setCorrectedInstruments] = useState(
    instruments.join(', ')
  );
  const [notes, setNotes] = useState('');

  const submitFeedback = async (approved: boolean) => {
    setIsSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        track_context_id: trackContextId,
        approved,
      };

      if (!approved) {
        if (correctedGenre !== genre) body.corrected_genre = correctedGenre;
        if (correctedArtists !== similarArtists.map(a => a.name).join(', ')) {
          body.corrected_artists = correctedArtists.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (correctedMoods !== moodTags.join(', ')) {
          body.corrected_mood_tags = correctedMoods.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (correctedInstruments !== instruments.join(', ')) {
          body.corrected_instruments = correctedInstruments.split(',').map(s => s.trim()).filter(Boolean);
        }
        if (notes) body.notes = notes;
      }

      const resp = await fetch(`${BACKBONE_URL}/api/classification/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = await resp.json();

      if (result.success) {
        setMode('submitted');
      } else {
        console.error('[ClassificationFeedback] Error:', result.error);
      }
    } catch (err) {
      console.error('[ClassificationFeedback] Network error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mode === 'submitted') {
    return (
      <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-800">
        <div className="flex items-center gap-2 text-green-400">
          <CheckCircle2 className="w-5 h-5" />
          <span className="text-sm font-medium">Thanks! This helps improve future classifications.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-800 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4 text-jelly-400" />
          <span className="text-sm font-medium">AI Classification</span>
        </div>
        {mode === 'review' && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => submitFeedback(true)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ThumbsUp className="w-3.5 h-3.5 mr-1" />
              )}
              Looks Good
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMode('editing')}
            >
              <Edit3 className="w-3.5 h-3.5 mr-1" />
              Fix This
            </Button>
          </div>
        )}
      </div>

      {/* Classification display */}
      <div className="flex flex-wrap gap-2">
        <span className="bg-jelly-500/20 text-jelly-400 rounded-full px-3 py-1 text-xs font-medium">
          {GENRE_LABELS[genre] || genre}
        </span>
        {moodTags.slice(0, 3).map(mood => (
          <span key={mood} className="bg-zinc-700/50 text-zinc-300 rounded-full px-3 py-1 text-xs">
            {mood}
          </span>
        ))}
      </div>

      {/* Artists */}
      {similarArtists.length > 0 && (
        <div className="text-xs text-zinc-400">
          <span className="text-zinc-500">Similar to: </span>
          {similarArtists.map(a => a.name).join(', ')}
        </div>
      )}

      {/* Instruments */}
      {instruments.length > 0 && (
        <div className="text-xs text-zinc-400">
          <span className="text-zinc-500">Instruments: </span>
          {instruments.join(', ')}
        </div>
      )}

      {/* Correction form */}
      {mode === 'editing' && (
        <div className="mt-3 pt-3 border-t border-zinc-700 space-y-3">
          {/* Genre dropdown */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Genre</label>
            <div className="relative">
              <select
                value={correctedGenre}
                onChange={e => setCorrectedGenre(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 appearance-none cursor-pointer focus:outline-none focus:border-jelly-500"
              >
                {GENRE_OPTIONS.map(g => (
                  <option key={g} value={g}>{GENRE_LABELS[g] || g}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-zinc-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Artists */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Similar Artists (comma-separated)</label>
            <input
              value={correctedArtists}
              onChange={e => setCorrectedArtists(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-jelly-500"
              placeholder="Artist 1, Artist 2, ..."
            />
          </div>

          {/* Mood */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Mood Tags (comma-separated)</label>
            <input
              value={correctedMoods}
              onChange={e => setCorrectedMoods(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-jelly-500"
              placeholder="calm, atmospheric, ..."
            />
          </div>

          {/* Instruments */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Instruments (comma-separated)</label>
            <input
              value={correctedInstruments}
              onChange={e => setCorrectedInstruments(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-jelly-500"
              placeholder="guitar, cello, ..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs text-zinc-500 block mb-1">Notes (optional)</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none focus:border-jelly-500"
              placeholder="Any additional context..."
            />
          </div>

          {/* Submit / Cancel */}
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={() => submitFeedback(false)}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
              )}
              Submit Correction
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setMode('review')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
